// ThreadsPatrolPage — Threads 海巡 work log for 門市人員.
//
// Staff search Threads for men's 穿搭/保養 posts worth answering, reply from
// THEIR OWN personal accounts in the Threads app, then come back here and
// record what they did. There is no send button and there never should be:
// Magnus's call 2026-09-11 is that the brand account @taichiu727 reads as him
// personally, so nobody else posts as it.
//
// Everything on this page serves one of three jobs:
//   找 — keyword chips that open a Threads search (the keywords are the ones
//        scripts/threads/patrol.py already scores well on);
//   寫 — a UTM link builder, so a reply that carries a metanoia.tw link is
//        measurable in analytics_events instead of invisible;
//   記 — the log itself, plus a duplicate check so two staff never answer the
//        same post as two unrelated strangers.
//
// Written mobile-first: this is used on a phone, standing at a counter.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ThreadsPatrolOpportunities from './ThreadsPatrolOpportunities';

// Keyword seeds. Mirrors the `default` + `grooming` profiles in the workspace
// scraper (scripts/threads/patrol_config.json) minus its season-specific
// entries — a hardcoded 春天穿搭 would be wrong for most of the year, and a
// stale chip wastes a search.
const SEARCH_KEYWORDS: { group: string; terms: string[] }[] = [
  {
    group: '穿搭（Metanoia）',
    terms: [
      '男生穿搭',
      '男裝推薦',
      '韓系男裝',
      '穿搭 求推薦',
      '男生衣服 推薦',
      '哪裡買 男裝',
      '男生 怎麼搭'
    ]
  },
  {
    group: '保養（MdoC）',
    terms: ['男生保養', '防曬推薦', '男生護膚', '控油', '素顏霜']
  }
];

// A 海巡 reply that lands a link should be traceable. utm capture is live on
// the storefront (src/services/analyticsService.ts) so these show up as
// utm_medium='patrol' in analytics_events.
const UTM_SOURCE = 'threads';
const UTM_MEDIUM = 'patrol';

const DAILY_TARGET = 5;

interface PatrolLog {
  id: number;
  post_url: string;
  post_author: string | null;
  post_excerpt: string | null;
  reply_text: string;
  link_url: string | null;
  user_id: number;
  user_name: string | null;
  created_at: string;
}

interface DuplicateRow {
  id: number;
  user_id: number;
  user_name: string | null;
  created_at: string;
}

// The API error handler responds { error: { status, message } }; a client
// reading data.message gets undefined (backend server.js:566). Read the real
// shape, and fall back rather than showing "undefined" to staff.
function apiMessage(err: any, fallback: string): string {
  return err?.response?.data?.error?.message || err?.response?.data?.message || fallback;
}

function threadsSearchUrl(term: string): string {
  return `https://www.threads.com/search?q=${encodeURIComponent(term)}&serp_type=recent`;
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString('zh-TW', {
      timeZone: 'Asia/Taipei',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return iso;
  }
}

// Campaign slug = who wrote it, so a week of logs can be read per person.
function campaignFor(name?: string | null): string {
  const slug = (name || 'staff')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `patrol_${slug || 'staff'}`;
}

const ThreadsPatrolPage: React.FC = () => {
  const { currentUser } = useAuth();

  const [postUrl, setPostUrl] = useState('');
  const [replyText, setReplyText] = useState('');
  const [postExcerpt, setPostExcerpt] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [duplicate, setDuplicate] = useState<DuplicateRow | null>(null);
  // Whose earlier reply it was decides what the warning should say: a colleague
  // is a real problem (two strangers pushing one shop), your own is just a
  // follow-up and perfectly fine.
  const [duplicateIsMine, setDuplicateIsMine] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [logs, setLogs] = useState<PatrolLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [todayCount, setTodayCount] = useState(0);
  const [weekCount, setWeekCount] = useState(0);

  const [linkInput, setLinkInput] = useState('');

  // 0 = 機會 (screener picks), 1 = 記錄 (the original find/link/log page).
  const [tab, setTab] = useState(0);

  const campaign = useMemo(() => campaignFor(currentUser?.name), [currentUser]);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const [logsRes, statsRes] = await Promise.all([
        api.get('/api/threads-patrol/logs', { params: { scope: 'all', days: 7, limit: 50 } }),
        api.get('/api/threads-patrol/stats', { params: { days: 7 } })
      ]);
      setLogs(logsRes.data?.logs || []);
      setTodayCount(statsRes.data?.today ?? 0);
      setWeekCount(statsRes.data?.window_total ?? 0);
    } catch (err: any) {
      setError(apiMessage(err, '讀取紀錄失敗，請重新整理'));
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // A new target post invalidates the previous duplicate verdict and the
  // "yes I really mean it" override — otherwise one confirmed duplicate would
  // silently wave through every post after it.
  const handleUrlChange = (value: string) => {
    setPostUrl(value);
    setDuplicate(null);
    setDuplicateIsMine(false);
    setAllowDuplicate(false);
    setError(null);
  };

  // Checked on blur, not on submit: staff should learn a post is taken BEFORE
  // they spend five minutes writing a reply to it.
  const checkDuplicate = async () => {
    if (!postUrl.trim()) return;
    setChecking(true);
    try {
      const res = await api.get('/api/threads-patrol/logs/check', {
        params: { post_url: postUrl.trim() }
      });
      const existing: DuplicateRow[] = res.data?.existing || [];
      setDuplicate(existing.length > 0 ? existing[0] : null);
      setDuplicateIsMine(
        existing.length > 0 && Number(existing[0].user_id) === Number(currentUser?.id)
      );
      if (res.data?.post_url) setPostUrl(res.data.post_url);
      setError(null);
    } catch (err: any) {
      setDuplicate(null);
      setDuplicateIsMine(false);
      setError(apiMessage(err, '連結看起來不是 Threads 貼文'));
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/api/threads-patrol/logs', {
        post_url: postUrl.trim(),
        reply_text: replyText,
        post_excerpt: postExcerpt || undefined,
        link_url: linkUrl || undefined,
        allow_duplicate: allowDuplicate
      });
      setPostUrl('');
      setReplyText('');
      setPostExcerpt('');
      setLinkUrl('');
      setAllowDuplicate(false);
      setDuplicate(null);
      setToast('已記錄');
      loadLogs();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        // Second press goes through — the message already names who got there
        // first, so this is an informed override, not a bypass.
        setAllowDuplicate(true);
      }
      setError(apiMessage(err, '記錄失敗，請再試一次'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/threads-patrol/logs/${id}`);
      setToast('已刪除');
      loadLogs();
    } catch (err: any) {
      setError(apiMessage(err, '刪除失敗'));
    }
  };

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast(label);
    } catch {
      setToast('複製失敗，請長按選取');
    }
  };

  // Appends our three utm params without trampling anything already on the URL.
  // Shared by the link builder below and the 機會 cards' 複製連結.
  const utmLink = useCallback((raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return '';
    try {
      const url = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
      url.searchParams.set('utm_source', UTM_SOURCE);
      url.searchParams.set('utm_medium', UTM_MEDIUM);
      url.searchParams.set('utm_campaign', campaign);
      return url.toString();
    } catch {
      return '';
    }
  }, [campaign]);

  const builtLink = useMemo(() => utmLink(linkInput), [linkInput, utmLink]);

  const canSubmit = postUrl.trim().length > 0 && !saving;

  // A greyed-out button with no reason is unusable on a phone: staff can see it
  // is dead but not which field is holding it. Name the missing piece instead of
  // making them guess (Magnus 2026-09-15: 「送出的按鈕沒辦法按」).
  const blockedBecause: string[] = [];
  if (!postUrl.trim()) blockedBecause.push('貼文連結');

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 780, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Threads 海巡
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        用自己的 Threads 帳號去回答別人的穿搭／保養問題，回覆完回到這裡記一筆。
      </Typography>

      {/* 今日進度 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={3} alignItems="baseline">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>
              {todayCount}
              <Typography component="span" variant="body2" color="text.secondary">
                {' '}/ {DAILY_TARGET}
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              今天
            </Typography>
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1 }}>
              {weekCount}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              近 7 天
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="fullWidth">
        <Tab label="機會" />
        <Tab label="記錄" />
      </Tabs>

      {tab === 0 && <ThreadsPatrolOpportunities onLogged={loadLogs} linkFor={utmLink} />}

      {tab === 1 && (<>
      {/* 找貼文 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          1. 找貼文
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          點關鍵字會開 Threads 搜尋（依最新排序）。挑<b>在問問題</b>、留言還不多的貼文最好回。
        </Typography>
        {SEARCH_KEYWORDS.map(group => (
          <Box key={group.group} sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {group.group}
            </Typography>
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {group.terms.map(term => (
                <Chip
                  key={term}
                  label={term}
                  size="small"
                  variant="outlined"
                  clickable
                  component="a"
                  href={threadsSearchUrl(term)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Paper>

      {/* 產生連結 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          2. 要附連結的話，先在這裡產生
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          貼上商品或穿搭頁網址，按複製，把產生出來的那條貼進你的回覆。
          這樣我們才看得出來有沒有人真的點進來。
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="https://metanoia.tw/products/..."
          value={linkInput}
          onChange={e => setLinkInput(e.target.value)}
        />
        {builtLink && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ flex: 1, wordBreak: 'break-all', color: 'text.secondary' }}
            >
              {builtLink}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={() => {
                copy(builtLink, '連結已複製');
                setLinkUrl(builtLink);
              }}
            >
              複製
            </Button>
          </Stack>
        )}
      </Paper>

      {/* 記錄 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          3. 記錄你回了什麼
        </Typography>

        <Stack spacing={2}>
          <TextField
            fullWidth
            label="貼文連結"
            placeholder="https://www.threads.com/share/XXXX"
            value={postUrl}
            onChange={e => handleUrlChange(e.target.value)}
            onBlur={checkDuplicate}
            helperText={checking ? '檢查中…' : '從貼文右上角「分享 → 複製連結」'}
            InputProps={{
              endAdornment: checking ? <CircularProgress size={16} /> : undefined
            }}
          />

          {duplicate && (
            <Alert severity={duplicateIsMine ? 'info' : 'warning'} sx={{ py: 0.5 }}>
              {duplicateIsMine ? (
                <>
                  這則你在 {formatWhen(duplicate.created_at)} 回過了。
                  如果是同一串的後續回覆，直接送出就好。
                </>
              ) : (
                <>
                  這則 <b>{duplicate.user_name || '有同事'}</b> 在 {formatWhen(duplicate.created_at)} 回過了。
                  建議換一則——同一則出現兩個不認識的人推同一家店很奇怪。
                </>
              )}
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="你的回覆內容"
            placeholder="把你在 Threads 上實際打的字貼進來"
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            helperText={replyText.trim().length > 0 ? `${replyText.trim().length} 字` : ' '}
          />

          <TextField
            fullWidth
            size="small"
            label="對方在問什麼（選填）"
            placeholder="例：165 想顯高，預算 1500"
            value={postExcerpt}
            onChange={e => setPostExcerpt(e.target.value)}
          />

          {linkUrl && (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              附上的連結：{linkUrl}
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <Button
            variant="contained"
            size="large"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {saving ? '記錄中…' : allowDuplicate ? '確定要記錄' : '送出記錄'}
          </Button>

          {!saving && blockedBecause.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              還缺：{blockedBecause.join('、')}
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* 全部紀錄 */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          全部紀錄（近 7 天，最新 50 筆）
        </Typography>
        {loadingLogs ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : logs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            還沒有紀錄。上面挑一個關鍵字開始吧。
          </Typography>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {logs.map(log => (
              <Box key={log.id} sx={{ py: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        記錄者：{log.user_name || '未具名'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatWhen(log.created_at)}
                      </Typography>
                      {log.post_author && (
                        <Typography variant="caption" color="text.secondary">
                          @{log.post_author}
                        </Typography>
                      )}
                      {log.link_url && <Chip label="有附連結" size="small" variant="outlined" />}
                    </Stack>
                    {log.post_excerpt && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        對方問：{log.post_excerpt}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                      {log.reply_text}
                    </Typography>
                    <Link
                      href={log.post_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, mt: 0.5 }}
                    >
                      看貼文 <OpenInNewIcon sx={{ fontSize: 12 }} />
                    </Link>
                  </Box>
                  {((currentUser?.role ?? 'admin') === 'admin' || Number(log.user_id) === Number(currentUser?.id)) && (
                    <IconButton
                      size="small"
                      aria-label="刪除這筆紀錄"
                      onClick={() => handleDelete(log.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
      </>)}

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ThreadsPatrolPage;
